import { fuzzySearchMutipleWords } from "@/lib/util";
import {
  FolderPlusIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { FolderIcon, FolderOpenIcon } from "@heroicons/react/24/solid";
import { format } from "date-fns";
import Link from "next/link";
import { OrderCarouselQuery } from "@/queries/autogenerate/graphql";
import { Dispatch, SetStateAction } from "react";
import InfiniteLoader from "../InfiniteLoader";
import { CurrentUser } from "../Page";
import { InventoryGrid } from "../styles/Layout";
import Tooltip from "../styles/Tooltip";
import RecipesGridItem from "./RecipesGridItem";
import { getPathToFolder, getRecipeFoldersMap } from "./util";

interface Props {
  user: CurrentUser;
  fragmentID?: string;
  recipes: OrderCarouselQuery["recipes"];
  sortedRecipes: OrderCarouselQuery["recipes"];
  selectedRecipes: string[];
  setSelectedRecipes: Dispatch<SetStateAction<string[]>>;
  limit: number;
  setLimit: Dispatch<SetStateAction<number>>;
  folder:
    | NonNullable<CurrentUser["school"]>["recipeCollections"][0]
    | null
    | undefined;
  sortedFolders: NonNullable<CurrentUser["school"]>["recipeCollections"];
  openCreateRecipeFolder: () => void;
  openEditRecipeFolder: (id: string) => void;
  openDeleteRecipeFolder: (id: string) => void;
  search: string;
}

const RecipesGrid: React.FC<Props> = ({
  user,
  fragmentID,
  recipes,
  sortedRecipes,
  selectedRecipes,
  setSelectedRecipes,
  limit,
  setLimit,
  folder,
  sortedFolders,
  openCreateRecipeFolder,
  openEditRecipeFolder,
  openDeleteRecipeFolder,
  search,
}) => {
  const fragmentRecipe =
    fragmentID && recipes.find((recipe) => recipe.id == fragmentID);

  // const recipeFoldersMap = getRecipeFoldersMap(
  //   user.school?.recipeCollections || [],
  //   recipes
  // );

  const displayFolders = search
    ? fuzzySearchMutipleWords(user.school?.recipeCollections || [], search, {
        keys: [
          "name",
          (item) => format(new Date(item.createdAt), "p EEEE LLLL do, yyyy"),
        ],
      })
    : sortedFolders.filter((rc) =>
        folder ? rc.belongsToId === folder.id : !rc.belongsToId
      );

  return (
    <div>
      {fragmentRecipe ? (
        <InventoryGrid itemWidth="500px">
          <RecipesGridItem
            user={user}
            recipe={fragmentRecipe}
            isFragment={true}
            isNative={true}
            sortedFolders={sortedFolders}
            folder={folder}
            search={search}
          />
        </InventoryGrid>
      ) : null}
      <InventoryGrid itemWidth="500px">
        {displayFolders.map((folder) => (
          <div
            key={folder.id}
            className="group relative border border-blue-400 border-opacity-50 bg-blue-50 transition-all hover:border-opacity-100"
          >
            <div
              className="group"
              style={{
                display: "flex",
                justifyContent: "start",
                alignItems: "start",
              }}
            >
              <Link prefetch={false} href={`/recipes/?folder=${folder.id}`}>
                <FolderIcon className="w-20 text-blue-400 group-hover:hidden" />
                <FolderOpenIcon className="hidden w-20 text-blue-400 group-hover:block" />
              </Link>
              <div
                style={{ margin: "5px" }}
                className="flex flex-1 items-start justify-between"
              >
                <Link prefetch={false} href={`/recipes/?folder=${folder.id}`}>
                  <div className="mb-5 ml-2">
                    <h3 className="font-medium">{folder.name}</h3>
                    <div className="mb-3 flex items-center gap-2 font-normal text-gray-400">
                      <span className="">
                        {}
                        {
                          recipes.filter(
                            (r) => r.recipeCollection?.id === folder.id
                          ).length
                        }{" "}
                        recipes
                      </span>
                      ·
                      <span className="">
                        {
                          sortedFolders.filter(
                            (f) => f.belongsToId === folder.id
                          ).length
                        }{" "}
                        folders
                      </span>
                    </div>
                    <small className="mb-2 block font-normal">
                      Created:{" "}
                      {format(
                        new Date(folder.createdAt),
                        "p EEEE LLLL do, yyyy"
                      )}
                    </small>
                    <small className="mb-2 block">
                      Updated:{" "}
                      {format(
                        new Date(folder.updatedAt),
                        "p EEEE LLLL do, yyyy"
                      )}
                    </small>

                    {search ? (
                      <div className="flex items-center gap-2 rounded border border-blue-400 border-opacity-50 bg-white p-1 text-sm">
                        <FolderIcon className="w-5 text-blue-400" />{" "}
                        {getPathToFolder(
                          sortedFolders.find((f) => f.id == folder.id),
                          sortedFolders
                        )
                          .map(
                            (id) =>
                              sortedFolders.find((c) => c.id === id)?.name ||
                              "unknown folder"
                          )
                          .join(" / ")}
                      </div>
                    ) : null}
                  </div>
                </Link>
                <div className="w-min">
                  <Tooltip content="Edit Folder">
                    <button onClick={() => openEditRecipeFolder(folder.id)}>
                      <PencilIcon className="m-1.5 mt-0 w-6 text-cyan-700 hover:text-cyan-900" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Delete Folder">
                    <button onClick={() => openDeleteRecipeFolder(folder.id)}>
                      <TrashIcon className="m-1.5 mt-0 w-6 text-orange-600 hover:text-orange-700" />
                    </button>
                  </Tooltip>

                  {/* {user.school &&
                        !(isStudent(user) && !user.school.permMakeOrders) ? (
                          <OrderDialogContext.Consumer>
                            {({ setOrderDialog, setRecipeToOrder }) => (
                              <Tooltip content="Order This Recipe">
                                <button
                                  className="z-10"
                                  onClick={() => {
                                    setRecipeToOrder(recipe.id);
                                    setOrderDialog(true);
                                  }}
                                >
                                  <ShoppingCartIcon className="m-1.5 mb-0 w-6 text-green-600 hover:text-green-700" />
                                </button>
                              </Tooltip>
                            )}
                          </OrderDialogContext.Consumer>
                        ) : null}

                        {isNative ? (
                          <DeleteRecipe
                            recipe={recipe}
                            user={user}
                            setSelectedRecipes={setSelectedRecipes}
                          />
                        ) : null}

                        {recipe.locked ? (
                          isTeacher(user) ? (
                            <Tooltip
                              content={
                                <div className="font-normal">
                                  <h2 className="mb-2 text-white">
                                    This Recipe Is Locked
                                  </h2>
                                  <p>
                                    Press this button to unlock it for students.
                                  </p>
                                </div>
                              }
                            >
                              <button
                                className=""
                                disabled={fetching}
                                onClick={async () => {
                                  await updateRecipe({
                                    where: { id: recipe.id },
                                    data: {
                                      locked: { set: false },
                                    },
                                  });
                                }}
                              >
                                {fetching ? (
                                  <div className="spinner h-6 w-6" />
                                ) : (
                                  <LockClosedIcon className="m-1.5 mt-0 w-6 text-gray-800 hover:text-gray-900" />
                                )}
                              </button>
                            </Tooltip>
                          ) : (
                            <Tooltip
                              content={
                                <div className="font-normal">
                                  <h2 className="mb-2 text-white">
                                    This Recipe Is Locked
                                  </h2>
                                  <p>
                                    Locked recipes are read only and cannot be
                                    edited by students.
                                  </p>
                                </div>
                              }
                            >
                              <LockClosedIcon className="m-1.5 mt-0 w-6 text-gray-800 hover:text-gray-900" />
                            </Tooltip>
                          )
                        ) : isTeacher(user) ? (
                          <Tooltip
                            content={
                              <div className="font-normal">
                                <h2 className="mb-2 text-white">
                                  Lock This Recipe
                                </h2>
                                <p>
                                  Locking a recipe makes it read only for
                                  students and prevents them making changes.
                                  They can still duplicate the recipe.
                                </p>
                              </div>
                            }
                          >
                            <button
                              disabled={fetching}
                              onClick={async () => {
                                await updateRecipe({
                                  where: { id: recipe.id },
                                  data: {
                                    locked: { set: true },
                                  },
                                });
                              }}
                            >
                              {fetching ? (
                                <div className="spinner h-6 w-6" />
                              ) : (
                                <LockOpenIcon className="m-1.5 mt-0 w-6 text-gray-800 hover:text-gray-900" />
                              )}
                            </button>
                          </Tooltip>
                        ) : null} */}
                </div>
              </div>
            </div>
          </div>
        ))}
        {sortedRecipes
          .filter((_, index) => index < limit)
          .map((r) => (
            <RecipesGridItem
              key={r.id}
              user={user}
              recipe={r}
              isNative={true}
              selectedRecipes={selectedRecipes}
              setSelectedRecipes={setSelectedRecipes}
              sortedFolders={sortedFolders}
              folder={folder}
              search={search}
            />
          ))}
        <div
          className="flex w-[250px] gap-4"
          style={{
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            boxShadow: "none",
          }}
        >
          <Tooltip content="Create Recipe" placement="bottom">
            <Link
              prefetch={false}
              href="/create-recipe"
              className="group flex flex-1 items-center justify-center p-16"
            >
              <PlusIcon className="w-18 transform-gpu text-gray-300 transition group-hover:scale-125 group-hover:text-gray-400" />
            </Link>
          </Tooltip>
          <Tooltip content="Create Folder" placement="bottom">
            <button
              onClick={openCreateRecipeFolder}
              className="group visible flex flex-1 items-center justify-center p-16"
            >
              <FolderPlusIcon className="w-18 transform-gpu text-gray-300 transition group-hover:scale-125 group-hover:text-gray-400" />
            </button>
          </Tooltip>
        </div>
      </InventoryGrid>
      <InfiniteLoader
        limit={limit}
        setLimit={setLimit}
        numberOfItems={sortedRecipes.length}
      />
    </div>
  );
};

export default RecipesGrid;
